import * as React from 'react';
import { Autocomplete, Box, TextField, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SuccessFailureIndicator } from '../../../subs/SuccessFailureIndicator';
import { GridColDef } from '@mui/x-data-grid';
import { WorkshopProductInterface, getWorkshopProductInterfaces } from '../../../types/WorkshopProductInterface';
import { WorkshopProduct, getWorkshopProducts } from '../../../types/WorkshopProducts';
import { Workshop, getWorkshops } from '../../../types/Workshop';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const WorkshopProductInterfacesAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [workshopproducts, setWorkshopProducts] = React.useState<WorkshopProduct[]>([]);
    const [workshopproduct, setWorkshopProduct] = React.useState<WorkshopProduct>();
    const [allData, setallData] = React.useState<WorkshopProductInterface[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllWorkshops = async () => {
            const allworkshopsResponse = await getWorkshops(new AbortController());
            if (allworkshopsResponse.failed) {
                showUserFeedback(allworkshopsResponse.errorText);
                return;
            }
            setWorkshops(allworkshopsResponse.result);
        }
        fetchAllWorkshops();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProducts = async () => {
            if (workshop === undefined || workshop.id === 0) return;
            const allWPResponse = await getWorkshopProducts(workshop.id, new AbortController());
            if (allWPResponse.failed) {
                showUserFeedback(allWPResponse.errorText);
                return;
            }
            setWorkshopProducts(allWPResponse.result);
        }
        fetchAllWorkshopProducts();
    }, [workshop, showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProductInterfaces = async () => {
            if (workshopproduct === undefined || workshopproduct.id === 0) return;
            const allWPIResponse = await getWorkshopProductInterfaces(workshopproduct.id, new AbortController());
            if (allWPIResponse.failed) {
                showUserFeedback(allWPIResponse.errorText);
                return;
            }
            setallData(allWPIResponse.result);
        }
        fetchAllWorkshopProductInterfaces();
    }, [workshopproduct, showUserFeedback]);

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'serverEndpoint',
            headerName: 'Server Endpoint',
            flex: 1,
        },
        {
            field: 'interfaceId',
            headerName: 'Interface ID',
            flex: 1,
        },
        {
            field: 'workshopProductId',
            headerName: 'Workshop Product ID',
            flex: 1,
        },
        {
            field: 'supportedByProduct',
            headerName: 'Supported By Product',
            type: 'boolean',
            flex: 1,
            editable: false,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.supportedByProduct} /> },
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsOptions' options={workshops.map(w => w.name)}
                    value={workshop ? workshop.name : ''}
                    onChange={(e, value) => setWorkshop(workshops.find(w => w.name === value))}
                    renderInput={(params) =>
                        <TextField name='wsOptions' multiline
                            {...params}
                            label={"Workshop"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        {
            workshop &&
            <Grid size={12} paddingBottom={10}>
                <FormControl required fullWidth>
                    <Autocomplete id='ac-wspOptions' options={workshopproducts.map(w => w.guid)}
                        value={workshopproduct ? workshopproduct.guid : ''}
                        onChange={(e, value) => setWorkshopProduct(workshopproducts.find(w => w.guid === value))}
                        renderInput={(params) =>
                            <TextField name='wspOptions' multiline
                                {...params}
                                label={"Workshop Product"}
                                sx={{
                                    '& .MuiOutlinedInput-root fieldset': {
                                        borderColor: 'divider',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                        borderColor: 'divider',
                                    },
                                }}
                            />
                        }
                    />
                </FormControl>
            </Grid>
        }
        {
            workshopproduct &&
            <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
                <EditableTable
                    columns={columns}
                    rows={allData}
                    setAllRows={setallData}
                />
            </Box>
        }
    </React.Fragment >
}